/**
 * **사장님 계정 로그인(휴대폰 번호·이메일)과 비밀번호 찾기 — 탭·입력 판정·요청 본문·응답 읽기 한 곳.** 의존성 없는 순수 모듈이다(Vue·
 * Element·`@/` 별칭 금지). 화면 문장은 `ownerCopy.js` 가 고른다. 콜팝 웹 가입(`ownerSignup.js`)도 본인확인 본문·거절 읽기를 여기서 쓴다.
 *
 * 정본: pos2-lite `src/domain/owner/owner-input.ts`(입력 판정·보내는 아이디·새 비밀번호 규칙)·`src/domain/owner/owner-wire.ts`(응답·거절 읽기).
 * 같은 사장님 계정으로 POS2 Lite·오더엔 멀티·관리 웹 세 곳에 로그인하므로 판정을 그 파일들과 같게 둔다. 리포·언어가 달라 추출하지 않는다 —
 * 정본을 고치면 이 사본도 같이 고친다. 서버 모양의 정본은 appserver `service/owner/`(`OwnerLoginView`·`OwnerStoreItem`·`OwnerIdentityView`·
 * `OwnerError`)다.
 *
 * 관리 웹이 다른 것: 탭이 셋이다(휴대폰 번호·이메일 + 지금의 아이디 로그인). 로그인 두 입구는 보안 인증을 싣는 관리 웹 입구
 * (`/admin/api/login/owner`·`…/store`)이고, 매장에 들어간 답은 아이디 로그인과 같은 `LoginResponse` 다. 휴대폰 번호의 숫자·종류는 이 리포의
 * `telNumber.js` 한 곳을 쓴다(Lite `normalizeTelNo`·`/^010\d{8}$/` 와 같은 답).
 */
// 확장자를 적는 이유: 단위 테스트가 앱 번들 밖(node)에서 읽는다 — node ESM 은 상대 경로 확장자를 추론하지 않는다.
import { TEL_KIND, telDigits, telKindOf } from './telNumber.js'

/** 로그인 탭 — 휴대폰 번호·이메일은 사장님 계정, 아이디는 지금의 계정 로그인(관리자·관리점·예전 매장 아이디) */
export const LOGIN_TAB = Object.freeze({ PHONE: 'phone', EMAIL: 'email', ID: 'id' })

/** 탭의 차례 — 화면에 이 순서로 선다 */
export const LOGIN_TABS = Object.freeze([LOGIN_TAB.PHONE, LOGIN_TAB.EMAIL, LOGIN_TAB.ID])

/** 처음 여는 탭 — 기억한 탭이 없거나 읽지 못하면 이것 */
export const DEFAULT_LOGIN_TAB = LOGIN_TAB.PHONE

/** 마지막에 로그인한 탭을 기억하는 브라우저 저장 열쇠(localStorage) — 편의다. 못 읽고 못 써도 화면은 처음 탭으로 돈다 */
export const LOGIN_TAB_STORAGE_KEY = 'ordern_login_tab'

/**
 * 사장님 계정 입구 — 로그인 둘은 관리 웹 입구(보안 인증), 본인확인·재설정·가입(주소 찾기·약관 판·가입)은 POS2 Lite 와 같은 로그인 전
 * 입구다(가입은 콜팝 웹 가입 `views/CallpopJoinView` — 계획 callpop-web-signup §11.2)
 */
export const OWNER_URL = Object.freeze({
  LOGIN: '/admin/api/login/owner',
  ENTER_STORE: '/admin/api/login/owner/store',
  IDENTITY: '/api/owner/identity',
  PASSWORD_RESET: '/api/owner/password/reset',
  ADDRESS_SEARCH: '/api/owner/address-search',
  TERMS: '/api/owner/terms',
  SIGNUP: '/api/owner/signup',
})

/**
 * 사장님 계정 요청의 axios config(네 입구 공통) — **토스트를 끄고**(실패는 폼의 오류 칸 한 곳이 말한다 — 아이디 로그인과 같은 규칙),
 * **403 이 세션을 끝내지 않는다**: 매장 고르기의 403 `STORE_NOT_LINKED` 는 「다시 골라 주세요」이지 세션 만료가 아니다(로그인 전이라 끝낼 세션도
 * 없다). 화면이 그 거절의 답을 가진다(`ownerCopy.ownerFailureText`) — `logoutOn403:false` 옵트아웃의 쓰임 그대로다.
 */
export const OWNER_REQUEST_CONFIG = Object.freeze({ showErrorMessage: false, logoutOn403: false })

/** 본인확인 표의 목적 — appserver `IdentityPurpose` 와 같은 낱말 */
export const IDENTITY_PURPOSE = Object.freeze({ PASSWORD_RESET: 'PASSWORD_RESET', SIGNUP: 'SIGNUP' })

/** 휴대폰 번호·이메일 탭인가 — 사장님 계정으로 로그인한다 */
export function isOwnerTab(tab) {
  return tab === LOGIN_TAB.PHONE || tab === LOGIN_TAB.EMAIL
}

/** 기억해 둔 값 → 탭. 모르는 값(옛 값·남이 쓴 값·없음)이면 처음 탭 */
export function loginTabOf(value) {
  return LOGIN_TABS.indexOf(value) > -1 ? value : DEFAULT_LOGIN_TAB
}

// ─── 입력(정본: owner-input.ts) ────────────────────────────────────────────────────────────────────

/** 휴대폰 번호 — 010 으로 시작하는 11자리(하이픈·띄어쓰기는 무시) */
export function isMobilePhone(value) {
  return telKindOf(telDigits(value)) === TEL_KIND.MOBILE
}

/** 이메일 꼴 — 서버가 소문자로 맞춘다 */
export function isEmailAddress(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(textOf(value).trim())
}

/**
 * 아이디 꼴 — 휴대폰 번호 꼴(숫자·하이픈·띄어쓰기만)도 이메일(`@`)도 아닌 값이다. 관리자·관리점 아이디(`admin`·`C260000169` 등)와 예전 매장
 * 아이디(S…)가 여기에 든다. **관리 웹에만 있는 판정**이다: 정본(owner-input.ts)은 예전 S… 아이디만 가르고 「예전 아이디」 단추로 보낸다. 관리 웹은
 * 처음 탭이 휴대폰이라, 아이디로 쓰던 분이 휴대폰 탭에 아이디를 쳐도 [아이디] 탭으로 안내한다(번호 꼴 오류 대신).
 */
export function isAccountIdLike(value) {
  const text = textOf(value).trim()
  return text.length > 0 && !/^[\d\s-]+$/.test(text) && text.indexOf('@') < 0
}

/** 휴대폰 탭에서 치는 중인 값이 번호 꼴인가(숫자·하이픈만) — 그때만 하이픈을 넣어 보인다. 아니면(예전 아이디 S… 등) 친 그대로 둔다 */
export function isPhoneTyping(value) {
  return /^[\d-]*$/.test(textOf(value))
}

/** 서버에 보내는 로그인 아이디 — 휴대폰은 숫자만, 이메일은 앞뒤 공백을 뗀 소문자 */
export function ownerLoginIdOf(tab, value) {
  return tab === LOGIN_TAB.PHONE ? telDigits(value) : textOf(value).trim().toLowerCase()
}

/** 로그인 입력의 문제 이름 — 문장은 `ownerCopy.ownerLoginInputText` */
export const OWNER_INPUT_PROBLEM = Object.freeze({
  PHONE: 'PHONE',
  EMAIL: 'EMAIL',
  ID_TAB: 'ID_TAB',
  PASSWORD_EMPTY: 'PASSWORD_EMPTY',
})

/**
 * 로그인 단추를 눌렀을 때 — 문제가 없으면 `null`. 아이디 꼴을 넣었으면 [OWNER_INPUT_PROBLEM.ID_TAB]([isAccountIdLike] — 관리 웹은 [아이디] 탭으로
 * 들어간다).
 *
 * @returns {?string} [OWNER_INPUT_PROBLEM] 의 하나
 */
export function ownerLoginInputProblem(tab, id, password) {
  if (isAccountIdLike(id)) return OWNER_INPUT_PROBLEM.ID_TAB
  if (tab === LOGIN_TAB.PHONE && !isMobilePhone(id)) return OWNER_INPUT_PROBLEM.PHONE
  if (tab === LOGIN_TAB.EMAIL && !isEmailAddress(id)) return OWNER_INPUT_PROBLEM.EMAIL
  if (textOf(password).length === 0) return OWNER_INPUT_PROBLEM.PASSWORD_EMPTY
  return null
}

/** 사장님 비밀번호 길이 — 서버 `AccountPasswordPolicy` 의 MIN·MAX 와 같다 */
export const OWNER_PASSWORD_MIN = 8
export const OWNER_PASSWORD_MAX = 60

/** 새 비밀번호의 문제 이름 — 문장은 `ownerCopy.ownerPasswordText` */
export const OWNER_PASSWORD_PROBLEM = Object.freeze({
  POLICY: 'POLICY',
  SAME_AS_ID: 'SAME_AS_ID',
  MISMATCH: 'MISMATCH',
})

/**
 * 새 비밀번호(비밀번호 찾기 ②) — 영문·숫자를 넣어 8~60자, 로그인 아이디(휴대폰·이메일)와 같으면 안 되고, 확인 칸과 같아야 한다.
 * `loginIds` 는 아는 것만(찾기에서는 가린 번호뿐이라 비운다 — 서버가 본다).
 *
 * @returns {?string} [OWNER_PASSWORD_PROBLEM] 의 하나(문제가 없으면 `null`)
 */
export function ownerPasswordProblem(password, confirm, loginIds) {
  const text = textOf(password)
  if (
    text.length < OWNER_PASSWORD_MIN ||
    text.length > OWNER_PASSWORD_MAX ||
    !/[A-Za-z]/.test(text) ||
    !/\d/.test(text)
  ) {
    return OWNER_PASSWORD_PROBLEM.POLICY
  }
  if (
    (loginIds || []).some((id) => typeof id === 'string' && id.length > 0 && id.toLowerCase() === text.toLowerCase())
  ) {
    return OWNER_PASSWORD_PROBLEM.SAME_AS_ID
  }
  if (text !== confirm) return OWNER_PASSWORD_PROBLEM.MISMATCH
  return null
}

// ─── 요청 본문 ─────────────────────────────────────────────────────────────────────────────────────

/** 사장님 로그인 — `POST /admin/api/login/owner` `{loginId, password, captcha}`(보안 인증을 묻지 않는 실행이면 `captcha` 는 `null`) */
export function ownerLoginBody(tab, loginId, password, captcha) {
  return { loginId: ownerLoginIdOf(tab, loginId), password, captcha: captcha || null }
}

/** 매장 고르기 — `POST /admin/api/login/owner/store` `{loginTicket, storeCd}` */
export function ownerEnterStoreBody(loginTicket, storeCd) {
  return { loginTicket, storeCd }
}

/**
 * 본인확인 표 — `POST /api/owner/identity` `{purpose, niceResult, product?}`. 비밀번호 찾기 ①은 `PASSWORD_RESET`(제품 없음), 가입 ①은
 * `SIGNUP` + 가입하는 제품(정본 owner-flows `verifyOwnerIdentity` 와 같은 본문)
 */
export function ownerIdentityBody(niceResult, purpose = IDENTITY_PURPOSE.PASSWORD_RESET, product = null) {
  return product ? { purpose, niceResult, product } : { purpose, niceResult }
}

/** 비밀번호 찾기 ② — `POST /api/owner/password/reset` `{ticket, newPassword}` */
export function ownerPasswordResetBody(ticket, newPassword) {
  return { ticket, newPassword }
}

// ─── 응답 읽기(정본: owner-wire.ts) ─────────────────────────────────────────────────────────────────

const MAX_OWNER_STORES = 200

/**
 * 사장님 로그인 200 `OwnerLoginView` → `{loginTicket, stores: [{storeCd, storeId, storeNm}]}`(계정 머리 `owner` 는 관리 웹이 쓰지 않는다 — 계정
 * 표시는 친 아이디다). 형식 밖이면 `null`(부르는 쪽이 「알 수 없는 실패」로 다룬다). 매장 한 줄이라도 형식 밖이면 목록 전체가 형식 밖이다
 * (Lite `readOwnerStores` 와 같다).
 */
export function ownerLoginViewOf(data) {
  const record = plainRecord(data)
  if (!record || !hasText(record.loginTicket)) return null
  const stores = ownerStoresOf(record.stores)

  return stores ? { loginTicket: record.loginTicket, stores } : null
}

function ownerStoresOf(raw) {
  if (!Array.isArray(raw) || raw.length > MAX_OWNER_STORES) return null
  const stores = raw.map(ownerStoreOf)
  return stores.some((store) => store === null) ? null : stores
}

function ownerStoreOf(raw) {
  const record = plainRecord(raw)
  const storeCd = record && positiveInteger(record.storeCd)
  if (!storeCd || typeof record.storeId !== 'string' || typeof record.storeNm !== 'string') return null
  return { storeCd, storeId: record.storeId, storeNm: record.storeNm }
}

/** 매장이 한 곳이면 그 매장 — 고르지 않고 바로 들어간다. 여럿(또는 없음)이면 `null` — 매장 고르기를 보인다 */
export function soleStoreCdOf(view) {
  return view && view.stores.length === 1 ? view.stores[0].storeCd : null
}

/**
 * 본인확인 200 `OwnerIdentityView` → `{ticket, verifiedName, phoneMasked, existingLoginIdMasked}`(본인확인 이름은 가입 ②의 대표자명 기본값).
 * 표가 없으면 `null`
 */
export function ownerIdentityOf(data) {
  const record = plainRecord(data)
  if (!record || !hasText(record.ticket)) return null
  const existing = plainRecord(record.existingOwner)
  return {
    ticket: record.ticket,
    verifiedName: hasText(record.verifiedName) ? record.verifiedName : null,
    phoneMasked: hasText(record.phoneMasked) ? record.phoneMasked : null,
    existingLoginIdMasked: existing && hasText(existing.loginIdMasked) ? existing.loginIdMasked : null,
  }
}

/** 비밀번호 재설정 200 `{loginId: 휴대폰, email}` → 로그인 휴대폰(숫자). 없으면 `null` */
export function ownerPasswordResetLoginIdOf(data) {
  const record = plainRecord(data)
  return record && hasText(record.loginId) ? record.loginId : null
}

/**
 * 사장님 계정 거절 봉투(`OwnerRejected` → `{code, message, …사유별 칸}`) → 화면이 읽는 칸. 사람에게 보일 말은 코드로 고른다(`ownerCopy`).
 *
 * 가입 거절의 두 칸(정본과 같다): `ceoMatches` — `BIZ_NUM_EXISTS` 에서 본인확인 이름이 그 매장 대표자와 같은가, `legacyStores` — 같은
 * 거절에 서버가 실은 그 사업자번호의 예전 매장(서버 칸 `stores`, 대표자가 같을 때만 — 없거나 형식 밖이면 빈 목록).
 *
 * @param {?number} status HTTP 상태
 * @param {?object} body 실패 응답 본문
 * @returns {{status: number, code: ?string, by: ?string, retryAfterSec: ?number, loginIdMasked: ?string, ceoMatches: ?boolean,
 *   legacyStores: Array<{accountId: string, storeNm: string, loginIdMasked: ?string}>}}
 */
export function ownerRejectionOf(status, body) {
  const record = plainRecord(body)
  const text = (key) => (record && typeof record[key] === 'string' ? record[key] : null)
  const by = text('by')
  const retry = record ? record.retryAfterSec : null
  const ceoMatches = record ? record.ceoMatches : null
  return {
    status: typeof status === 'number' ? status : 0,
    code: text('code'),
    by: by === 'PHONE' || by === 'EMAIL' ? by : null,
    retryAfterSec: typeof retry === 'number' && Number.isFinite(retry) && retry >= 0 ? Math.ceil(retry) : null,
    loginIdMasked: text('loginIdMasked'),
    ceoMatches: typeof ceoMatches === 'boolean' ? ceoMatches : null,
    legacyStores: ownerLegacyMatchesOf(record && record.stores) || [],
  }
}

/**
 * 사업자등록번호로 찾은 예전 매장 목록(정본 owner-wire `readOwnerLegacyMatches`) — 줄마다 `{accountId(S…), storeNm, loginIdMasked}`
 * (`loginIdMasked` 는 그 매장이 이미 연결된 사장님 계정, 연결 전이면 `null`). 배열이 아니거나 한 줄이라도 형식 밖이면 `null`
 */
export function ownerLegacyMatchesOf(raw) {
  if (!Array.isArray(raw) || raw.length > MAX_OWNER_STORES) return null
  const matches = raw.map((item) => {
    const record = plainRecord(item)
    if (!record || !hasText(record.accountId) || typeof record.storeNm !== 'string') return null
    return {
      accountId: record.accountId,
      storeNm: record.storeNm,
      loginIdMasked: hasText(record.loginIdMasked) ? record.loginIdMasked : null,
    }
  })
  return matches.some((match) => match === null) ? null : matches
}

/**
 * 관리 웹 로그인 관문의 거절인가 — 보안 인증(`CAPTCHA_FAILED`)·조직 비활성처럼 `401 {error, code}` 로 오는 것. 아이디 로그인과 같이 다룬다
 * (`loginFailure.loginFailureText` — 서버 `error` 문장 그대로). 사장님 계정 거절은 `{code, message}` 라 `error` 칸이 없다.
 */
export function isLoginGateRejection(body) {
  const record = plainRecord(body)
  return !!record && hasText(record.code) && hasText(record.error)
}

function plainRecord(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : null
}

function positiveInteger(value) {
  if (typeof value === 'number' && Number.isSafeInteger(value) && value > 0) return value
  if (typeof value === 'string' && /^\d{1,15}$/.test(value)) {
    const parsed = Number(value)
    return parsed > 0 ? parsed : null
  }
  return null
}

function textOf(value) {
  return value == null ? '' : String(value)
}

function hasText(value) {
  return typeof value === 'string' && value.trim() !== ''
}
